import { Fragment, useEffect, useState } from 'react';
import { Wifi } from 'lucide-react';
import { fetchChargerNetwork, getDisplayErrorMessage, saveChargerNetworkLink, type ChargerNetworkRow, type ChargerNetworkView } from '@/lib/api';
import { LINK_OPTIONS, NETWORK_ID_MAX, SIM_OPTIONS, draftOf, draftProblem, isDirty, patchOf, rebaseDrafts, rowWarning, siteTime, watcherLine, type NetworkDraft } from '@/lib/chargerNetwork';
import formStyles from './Form.module.css';
import styles from './ChargerNetworkCard.module.css';

/**
 * The location page's Charger network card (Warren, 4 Nov 2026, The Vered: "Record in Admin, for each Vered charger: link (Wi-Fi
 * or SIM), SIM paused or active, and a field for breaker and panel ID"; ruling S3). One row per charger, each with its own Save.
 *
 * EVI staff only, on the Api's own can_manage_network, never a persona: anyone else, and a read that fails, sees nothing at all.
 *
 * It records. Saving changes no SIM and sends nothing to a charger: until 1NCE credentials exist EVI runs the fallback by hand in
 * the portal and writes down here what it did. The Watcher switch is what lets the 1NCE watcher act on a charger; the seed leaves
 * it off, and turning it on clears why the watcher last turned it off.
 */
interface ChargerNetworkCardProps {
  locationId: string;
}

export function ChargerNetworkCard({ locationId }: ChargerNetworkCardProps) {
  const [view, setView] = useState<ChargerNetworkView | null>(null);
  const [drafts, setDrafts] = useState<Record<string, NetworkDraft>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setView(null);
    setDrafts({});
    setErrors({});
    setSaved(null);
    fetchChargerNetwork(locationId)
      .then((v) => { if (!cancelled) setView(v); })
      .catch(() => { if (!cancelled) setView(null); });
    return () => { cancelled = true; };
  }, [locationId]);

  if (!view || !view.can_manage_network) return null;

  const draftFor = (row: ChargerNetworkRow): NetworkDraft => drafts[row.charger_id] ?? draftOf(row);
  const setDraft = (row: ChargerNetworkRow, change: Partial<NetworkDraft>): void => {
    setDrafts((prev) => ({ ...prev, [row.charger_id]: { ...draftFor(row), ...change } }));
    setErrors((prev) => ({ ...prev, [row.charger_id]: '' }));
    setSaved(null);
  };
  const replaceRow = (next: ChargerNetworkRow): void => {
    setView((prev) => (prev ? { ...prev, chargers: prev.chargers.map((r) => (r.charger_id === next.charger_id ? next : r)) } : prev));
    setDrafts((prev) => {
      const { [next.charger_id]: _done, ...rest } = prev;
      return rest;
    });
  };

  const save = async (row: ChargerNetworkRow): Promise<void> => {
    const draft = draftFor(row);
    const problem = draftProblem(row, draft);
    if (problem) { setErrors((prev) => ({ ...prev, [row.charger_id]: problem })); return; }
    setSaving(row.charger_id);
    setErrors((prev) => ({ ...prev, [row.charger_id]: '' }));
    setSaved(null);
    try {
      replaceRow(await saveChargerNetworkLink(locationId, row.charger_id, patchOf(row, draft)));
      setSaved(row.charger_id);
    } catch (err) {
      const message = await getDisplayErrorMessage(err, 'Save failed. Please try again.');
      setErrors((prev) => ({ ...prev, [row.charger_id]: message }));
      // The watcher or another person moved this charger: show it as it is now, and keep only what the person changed.
      if ((err as { response?: { status?: number } })?.response?.status === 409) {
        const fresh = await fetchChargerNetwork(locationId).catch(() => null);
        if (fresh?.can_manage_network) {
          setDrafts((prev) => rebaseDrafts(view.chargers, { ...prev, [row.charger_id]: draft }, fresh.chargers));
          setView(fresh);
        }
      }
    } finally {
      setSaving(null);
    }
  };

  return (
    <section className={styles.card} aria-labelledby="charger-network-title">
      <h2 id="charger-network-title" className={styles.title}><Wifi size={15} /> Charger network</h2>
      <p className={styles.hint}>
        EVI staff only. This records what was done: saving changes no SIM. Turn a SIM on or off in the 1NCE portal, then record it here. Times are the site's own.
      </p>
      {watcherLine(view.watcher_mode) && <p className={styles.hint}>{watcherLine(view.watcher_mode)}</p>}

      {view.chargers.length === 0 ? (
        <p className={styles.hint}>No chargers at this location.</p>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th scope="col">Charger</th>
              <th scope="col">Link</th>
              <th scope="col">SIM</th>
              <th scope="col">SIM ending</th>
              <th scope="col">Breaker ID</th>
              <th scope="col">Panel ID</th>
              <th scope="col">Watcher</th>
              <th scope="col">Last event</th>
              <th scope="col"><span className={styles.srOnly}>Save</span></th>
            </tr>
          </thead>
          <tbody>
            {view.chargers.map((row) => {
              const draft = draftFor(row);
              const dirty = isDirty(row, draft);
              const busy = saving === row.charger_id;
              const warning = rowWarning(row, view.watcher_mode, view.time_zone);
              const note = (row.fallback_off_reason && !row.fallback_enabled) || warning || errors[row.charger_id] || saved === row.charger_id;
              return (
                <Fragment key={row.charger_id}>
                <tr className={styles.row}>
                  <th scope="row" className={styles.name}>{row.name}</th>
                  <td data-label="Link">
                    <select className={formStyles.select} aria-label={`${row.name} link`} value={draft.link} onChange={(e) => setDraft(row, { link: e.target.value as NetworkDraft['link'] })}>
                      {LINK_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </td>
                  <td data-label="SIM">
                    {row.has_sim ? (
                      <select className={formStyles.select} aria-label={`${row.name} SIM`} value={draft.sim} onChange={(e) => setDraft(row, { sim: e.target.value as NetworkDraft['sim'] })}>
                        {SIM_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    ) : <span className={styles.none}>No SIM</span>}
                  </td>
                  <td data-label="SIM ending" className={styles.ending}>{row.sim_ending ?? <span className={styles.none}>None</span>}</td>
                  <td data-label="Breaker ID">
                    <input className={`${formStyles.input} ${styles.idInput}`} aria-label={`${row.name} breaker ID`} value={draft.breaker_id} maxLength={NETWORK_ID_MAX} autoComplete="off" onChange={(e) => setDraft(row, { breaker_id: e.target.value })} />
                  </td>
                  <td data-label="Panel ID">
                    <input className={`${formStyles.input} ${styles.idInput}`} aria-label={`${row.name} panel ID`} value={draft.panel_id} maxLength={NETWORK_ID_MAX} autoComplete="off" onChange={(e) => setDraft(row, { panel_id: e.target.value })} />
                  </td>
                  <td data-label="Watcher">
                    {row.has_sim ? (
                      <label className={styles.watcher}>
                        <input type="checkbox" aria-label={`${row.name} watcher`} checked={draft.fallback} onChange={(e) => setDraft(row, { fallback: e.target.checked })} />
                        <span>{draft.fallback ? 'On' : 'Off'}</span>
                      </label>
                    ) : <span className={styles.none}>No SIM</span>}
                  </td>
                  <td data-label="Last event" className={styles.event}>
                    {row.last_event ? (
                      <>
                        <span className={row.last_event.failed ? styles.eventFailed : undefined}>{row.last_event.summary}</span>
                        <span className={styles.eventAt}>{siteTime(row.last_event.at, view.time_zone)}</span>
                      </>
                    ) : <span className={styles.none}>None</span>}
                  </td>
                  <td className={styles.saveCell}>
                    <button type="button" className={`${formStyles.btnPrimary} ${styles.save}`} disabled={!dirty || busy} onClick={() => { void save(row); }}>
                      {busy ? 'Saving...' : 'Save'}
                    </button>
                  </td>
                </tr>
                {note && (
                  <tr className={styles.noteRow}>
                    <td className={styles.note} colSpan={9}>
                      {row.fallback_off_reason && !row.fallback_enabled && <span className={styles.offReason}>{row.fallback_off_reason}</span>}
                      {warning && <span className={styles.offReason}>{warning}</span>}
                      {errors[row.charger_id] && <span className={styles.alertErr} role="alert">{errors[row.charger_id]}</span>}
                      {saved === row.charger_id && !errors[row.charger_id] && <span className={styles.alertOk}>Saved.</span>}
                    </td>
                  </tr>
                )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}
